import { renderToString } from 'react-dom/server';
import { injectable } from 'inversify';

import type { ISsrService } from './types';

const MAX_DEPTH = 10;

@injectable()
class SsrService implements ISsrService {
  _promises: Array<Promise<any>> = [];

  _keys: Record<string, any> = {};

  /**
   * SERVER
   * Рендер приложения на сервере с ожиданием асинхронных действий.
   * @param JSX React приложение
   * @returns {Promise<string>}
   */
  async render(jsx: JSX.Element) {
    let result = '';
    let maxDepth = MAX_DEPTH;
    do {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(this._promises);
      this._promises = [];
      result = renderToString(jsx);
    } while (this._promises.length && --maxDepth > 0);
    // eslint-disable-next-line no-empty
    return result;
  }

  /**
   * SERVER
   * Добавление ожидания действия перед рендером на сервере
   * @param callback {Promise|Function}
   * @param key {String} Ключ, под которым будут загружаться данные
   * @return {Promise} Промис переданного callback
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  async prepare(callback: (payload: boolean) => any, key: string) {
    if (key) {
      if (!this.hasPrepare(key)) {
        this._keys[key] = callback instanceof Promise ? callback : callback(true);
        this._promises.push(this._keys[key]);
      }
      return this._keys[key];
    }
    return callback instanceof Promise ? callback : callback(true);
  }

  /**
   * SERVER & CLIENT
   * Проверка существования ожидания по ключу, с которым вызывался prepare() на сервере
   * @param key {String}
   * @return {Boolean}
   */
  hasPrepare(key: string) {
    return !!key && key in this._keys;
  }

  /**
   * CLIENT
   * Удаление ожидания по ключу
   * Используется на клиенте, чтобы разблокировать логику работы с состоянием
   * @param key {String}
   */
  deletePrepare(key: string) {
    if (this.hasPrepare(key)) {
      delete this._keys[key];
    }
  }

  /**
   * SERVER
   * Ключи текущих ожиданий
   * @return {Array<String>}
   */
  getPrepareKeys() {
    return Object.keys(this._keys);
  }

  /**
   * CLIENT
   * Установка включений ожиданий, с целью предотвратить их повторное исполнение или заблокировать логику на клиенте
   * @param keys {Array<String>}
   */
  setPrepareKeys(keys: string[]) {
    for (const key of keys) {
      this._keys[key] = true;
    }
  }
}

export default SsrService;
